import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import RowLayout from '../../../../components/Layouts/RowLayout';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import {SMCCheckbox} from '../../../../components/Simple/SMCCheckbox';
import SMCNumberInput from '../../../../components/Simple/SMCNumberInput';
import {SMCSelect, SMCOption} from '../../../../components/Simple/SMCSelect';
import Indentation from '../../../../components/Simple/Indentation';
import {SMCRadio, SMCRadioGroup} from '../../../../components/Simple/SMCRadio';
import SMCDayPickerInput from '../../../../components/Simple/SMCDayPickerInput';
import Sepperator from '../../../../components/Simple/Sepperator';
import dayjs from 'dayjs';
import {dateAndTimeToString} from '../../../../utils/utils';
import {UserGetSchema} from '../../../../api/DBSchema/User';

const pageSchema = UserGetSchema.pages.expiration;
const pageId = pageSchema.pageId;

function AdministratorExpiration({
    administratorObject,
    setAdministratorData
}) {
    const { t } = useTranslation();

    const pageData = administratorObject[pageId];
    const setPageValue = useCallback((paramName, value) => {
        setAdministratorData([pageId, paramName], value);
    }, [setAdministratorData]);

    const hasExpiration              = pageData[pageSchema.hasExpiration];
    const startDate                  = pageData[pageSchema.startDate];
    const startDateAsDateObject      = useMemo(() => startDate ? new Date(startDate) : null, [startDate]);
    const startMode                  = pageData[pageSchema.startMode];
    const subscriptionPeriod         = pageData[pageSchema.subscriptionPeriod];
    const subscriptionPeriodUnit     = pageData[pageSchema.subscriptionPeriodUnit];
    const administratorFirstLogin             = pageData[pageSchema.administratorFirstLogin];
    const administratorFirstLoginAsDateObject = useMemo(() => administratorFirstLogin ? new Date(administratorFirstLogin) : null, [administratorFirstLogin]);

    const administratorExpiryDate = useMemo(() => {
        // If there is no experation, there is no expiration date
        if (!hasExpiration) {
            return null;
        }
        // If start mode is "first login" and administrator has a first login date
        if (startMode === pageSchema.startModeValues.firstLogin && administratorFirstLoginAsDateObject) {
            if (subscriptionPeriodUnit === pageSchema.subscriptionPeriodUnitValues.hours) {
                return dayjs(administratorFirstLoginAsDateObject).add(subscriptionPeriod, 'hour').toDate();
            } else if (subscriptionPeriodUnit === pageSchema.subscriptionPeriodUnitValues.days) {
                return dayjs(administratorFirstLoginAsDateObject).add(subscriptionPeriod, 'day').toDate();
            } else if (subscriptionPeriodUnit === pageSchema.subscriptionPeriodUnitValues.months) {
                return dayjs(administratorFirstLoginAsDateObject).add(subscriptionPeriod, 'month').toDate();
            } else {
                return null;
            }
        }
        // If start mode is "specified date" and start date is set
        else if (startDateAsDateObject) {
            if (subscriptionPeriodUnit === pageSchema.subscriptionPeriodUnitValues.hours) {
                return dayjs(startDateAsDateObject).add(subscriptionPeriod, 'hour').toDate();
            } else if (subscriptionPeriodUnit === pageSchema.subscriptionPeriodUnitValues.days) {
                return dayjs(startDateAsDateObject).add(subscriptionPeriod, 'day').toDate();
            } else if (subscriptionPeriodUnit === pageSchema.subscriptionPeriodUnitValues.months) {
                return dayjs(startDateAsDateObject).add(subscriptionPeriod, 'month').toDate();
            } else {
                return null;
            }
        }
    }, [hasExpiration, startDateAsDateObject, startMode, subscriptionPeriodUnit, administratorFirstLoginAsDateObject, subscriptionPeriod]);

    return (
        <EditorLayout>
            <RowLayout spacing="6px">
                <SMCCheckbox
                    inline
                    checked={hasExpiration}
                    onChange={e => setPageValue(pageSchema.hasExpiration, e.target.checked)}
                />
                <SMCLabel inline>{t('SMP.USERS.TABLE.PAGES.EXPIRATION.LINE1')}</SMCLabel>
                <SMCNumberInput
                    inline
                    width="65px"
                    value={subscriptionPeriod}
                    onChange={e => setPageValue(pageSchema.subscriptionPeriod, parseInt(e.target.value))}
                    disabled={!hasExpiration}
                />
                <SMCSelect
                    inline
                    width="100px"
                    value={subscriptionPeriodUnit}
                    onChange={e => setPageValue(pageSchema.subscriptionPeriodUnit, e.target.value)}
                    disabled={!hasExpiration}
                >
                    <SMCOption value={pageSchema.subscriptionPeriodUnitValues.hours}>{t('SMP.USERS.TABLE.PAGES.EXPIRATION.LINE1.HOURS')}</SMCOption>
                    <SMCOption value={pageSchema.subscriptionPeriodUnitValues.days}>{t('SMP.USERS.TABLE.PAGES.EXPIRATION.LINE1.DAYS')}</SMCOption>
                    <SMCOption value={pageSchema.subscriptionPeriodUnitValues.months}>{t('SMP.USERS.TABLE.PAGES.EXPIRATION.LINE1.MONTHS')}</SMCOption>
                </SMCSelect>
            </RowLayout>
            {
                hasExpiration && (
                    <Indentation>
                        <RowLayout>
                            <SMCLabel inline width="100px">{t('SMP.USERS.TABLE.PAGES.EXPIRATION.START_FROM')}</SMCLabel>
                            <SMCRadioGroup
                                value={startMode}
                                onChange={e => setPageValue(pageSchema.startMode, e.target.value)}
                            >
                                <SMCRadio
                                    value={pageSchema.startModeValues.firstLogin}
                                    label={t('SMP.USERS.TABLE.PAGES.EXPIRATION.FIRST_LOGIN')}
                                />
                                <SMCRadio
                                    value={pageSchema.startModeValues.specifiedDate}
                                    label={t('SMP.USERS.TABLE.PAGES.EXPIRATION.SPECIFIED_DATE')}
                                />
                            </SMCRadioGroup>
                        </RowLayout>
                        {
                            startMode === pageSchema.startModeValues.specifiedDate && (
                                <Indentation indentation={100}>
                                    <RowLayout>
                                        <SMCDayPickerInput
                                            value={startDateAsDateObject}
                                            onChange={date => setPageValue(pageSchema.startDate, date.getTime())}
                                            placeholder={t('SMP.USERS.TABLE.PAGES.EXPIRATION.SPECIFIED_DATE')}
                                            showTimeSelect={true}
                                            width={150}
                                        />
                                    </RowLayout>
                                </Indentation>
                            )
                        }
                        <Sepperator />
                        <RowLayout>
                            <SMCLabel width={100} inline>{t('SMP.USERS.TABLE.PAGES.EXPIRATION.FIRST_LOGIN')}</SMCLabel>
                            <SMCLabel inline>{administratorFirstLoginAsDateObject ? dateAndTimeToString(administratorFirstLoginAsDateObject, true) : 'N/A'}</SMCLabel>
                        </RowLayout>
                        <RowLayout>
                            <SMCLabel width={100} inline>{t('SMP.USERS.TABLE.PAGES.EXPIRATION.EXPIRES_ON')}</SMCLabel>
                            <SMCLabel inline>{administratorExpiryDate ? dateAndTimeToString(administratorExpiryDate, true) : 'N/A'}</SMCLabel>
                        </RowLayout>
                    </Indentation>
                )
            }
        </EditorLayout>
    )
}

export default AdministratorExpiration;